import { create } from "zustand";
import {
  Activity,
  ActivityFilters,
  activityService,
} from "../services/activityService";

/**
 * Activity store (legacy Zustand — being migrated to React Query's
 * useActivities, see hooks/useActivities.ts).
 *
 * Day 14 HOTFIX — request-storm guard:
 *   The Header mounts this store and polls it; the Dashboard also calls
 *   fetchActivities. With no concurrency protection, any caller that
 *   fires fetchActivities on a render (or a misconfigured effect) could
 *   spawn an unbounded request loop — observed as hundreds of GET
 *   /activity calls per second, which froze the Settings page.
 *
 *   Two guards now make a runaway loop structurally impossible:
 *     1. IN-FLIGHT GUARD — if a fetch is already running, a new call is
 *        a no-op (returns the existing promise). Overlapping fetches
 *        can't stack.
 *     2. MIN-INTERVAL GUARD — back-to-back fetches within MIN_FETCH_MS
 *        are skipped. A render loop calling fetchActivities() thousands
 *        of times collapses to at most one call per MIN_FETCH_MS.
 *
 *   A caller can force a fetch (bypassing the interval guard, but NOT
 *   the in-flight guard) by passing { force: true } — used by explicit
 *   "Refresh" buttons.
 */

// Minimum gap between automatic fetches. A render loop can call as fast
// as it likes; only one request per this window actually goes out.
const MIN_FETCH_MS = 5_000;

interface ActivityStore {
  activities: Activity[];
  total: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  filters: ActivityFilters;

  // Internal guard state (not for component consumption)
  _inFlight: Promise<void> | null;
  _lastFetchAt: number;

  fetchActivities: (
    filters?: ActivityFilters,
    opts?: { force?: boolean },
  ) => Promise<void>;
  setFilters: (filters: Partial<ActivityFilters>) => void;
  clearFilters: () => void;
}

export const useActivityStore = create<ActivityStore>((set, get) => ({
  activities: [],
  total: 0,
  currentPage: 1,
  pageSize: 20,
  loading: false,
  error: null,
  filters: {},

  _inFlight: null,
  _lastFetchAt: 0,

  fetchActivities: async (filters = {}, opts = {}) => {
    const state = get();

    // GUARD 1 — in-flight: never run two fetches at once. Return the
    // existing promise so awaiters still resolve correctly.
    if (state._inFlight) {
      return state._inFlight;
    }

    // GUARD 2 — min-interval: skip automatic refetches that arrive too
    // soon after the last one. `force: true` (explicit refresh) bypasses
    // this, but still respects the in-flight guard above.
    const since = Date.now() - state._lastFetchAt;
    if (!opts.force && since < MIN_FETCH_MS) {
      return;
    }

    const run = (async () => {
      set({ loading: true, error: null });
      try {
        const currentFilters = { ...get().filters, ...filters };
        const response = await activityService.getActivities(currentFilters);
        set({
          activities: response.activities,
          total: response.pagination.total,
          currentPage: response.pagination.page,
          pageSize: response.pagination.limit,
          loading: false,
        });
      } catch (error) {
        console.error("Error fetching activities:", error);
        set({ error: "Failed to fetch activities", loading: false });
      } finally {
        // Clear the in-flight handle and stamp the time AFTER completion,
        // so the interval window measures gap-between-completions.
        set({ _inFlight: null, _lastFetchAt: Date.now() });
      }
    })();

    // Register the in-flight promise synchronously, before any await, so a
    // second call landing in the same tick sees it and bails.
    set({ _inFlight: run });
    return run;
  },

  setFilters: (newFilters) => {
    const currentFilters = get().filters;
    const merged = { ...currentFilters, ...newFilters };
    set({ filters: merged });
    // Filter changes are user-initiated → force past the interval guard
    // (but the in-flight guard still prevents overlap).
    void get().fetchActivities(merged, { force: true });
  },

  clearFilters: () => {
    set({ filters: {} });
    void get().fetchActivities({}, { force: true });
  },
}));
